import { message } from 'antd';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { Input } from '@/components/shadcn/ui/input';
import { Label } from '@/components/shadcn/ui/label';
import useProductStore from '@/features/Product/store/ProductStore';
import { Button } from '@/shared/ui/button';
import useUserStore from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import type { ScrapingJobSelection } from '../../../model/types';
import { buildJobSelectionPayload } from '../build-job-selection-payload';
import { useScrapingJobSelection } from '../use-scraping-job-selection';
import { useScrapingJobsQuery } from '../use-scraping-jobs-query';
import { ScrapingJobTable } from './scraping-job-table';

type ScrapingJobSectionProps = {
  onJobsSelected: (params: {
    jobs: ScrapingJobSelection[];
    productFilter: FilterKeyValue[];
    dateRange: { startDate: string; endDate: string };
  }) => void;
};

/**
 * 폼 내 인라인 스크래핑 job 선택 섹션
 *
 * Job 선택 시 product/dateRange auto-fill 반영
 */
export const ScrapingJobSection = ({
  onJobsSelected,
}: ScrapingJobSectionProps) => {
  const [onlyMyJobs, setOnlyMyJobs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const productMap = useProductStore((s) => s.productMap);
  const userId = useUserStore((s) => s.user?.user_id);
  const members = useWorkspaceStore((s) => s.currWorkspaceMembers);

  // email → 이름 매핑
  const userIdNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
      if (name) map.set(m.user_id, name);
    }
    return map;
  }, [members]);

  const {
    jobs,
    allJobs,
    allJobsCount,
    isLoading,
    isError,
    refetch,
    hasMore,
    loadMore,
    isFetchingMore,
  } = useScrapingJobsQuery({
    onlyMyJobs,
    currentUserId: userId,
    searchTerm,
  });

  const {
    rowSelection,
    selectedJobs,
    handleClickRow,
    handleToggleAll,
    isAllSelected,
    isIndeterminate,
  } = useScrapingJobSelection({ allJobs, visibleJobs: jobs });

  // 선택된 job들로부터 product filter + dateRange auto-fill
  const handleApplySelection = () => {
    if (selectedJobs.length === 0) return;

    const payload = buildJobSelectionPayload(selectedJobs, productMap);

    if (payload.unmatchedCount > 0) {
      message.info(
        `${payload.productFilter.length} out of ${payload.productFilter.length + payload.unmatchedCount} products matched`,
      );
    }

    onJobsSelected({
      jobs: payload.jobs,
      productFilter: payload.productFilter,
      dateRange: payload.dateRange,
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label>Scraping Jobs</Label>
          <span className="text-xs text-muted-foreground">
            {isLoading ? '...' : `(${allJobsCount})`}
          </span>
          {selectedJobs.length > 0 && (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {selectedJobs.length} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Checkbox
            id="only-my-jobs"
            checked={onlyMyJobs}
            onCheckedChange={(checked) => setOnlyMyJobs(!!checked)}
          />
          <Label
            htmlFor="only-my-jobs"
            className="text-xs font-normal text-muted-foreground"
          >
            My jobs only
          </Label>
        </div>
      </div>

      {/* 검색 입력 */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by title or product..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 text-sm"
        />
      </div>

      {isError ? (
        <div className="flex flex-col items-center gap-2 py-4">
          <p className="text-xs text-destructive">
            Failed to load scraping jobs
          </p>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => refetch()}
            className="h-auto p-0 text-xs"
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <ScrapingJobTable
            jobs={jobs}
            rowSelection={rowSelection}
            onClickRow={handleClickRow}
            onToggleAll={handleToggleAll}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            isLoading={isLoading}
            isFetchingMore={isFetchingMore}
            hasSearchTerm={!!searchTerm}
            memberNameMap={userIdNameMap}
          />

          {hasMore && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadMore}
              disabled={isFetchingMore}
              className="w-full text-xs"
            >
              {isFetchingMore ? 'Loading...' : 'Load more'}
            </Button>
          )}

          <div className="flex items-center justify-end">
            {selectedJobs.length > 0 && (
              <Button
                type="button"
                size="sm"
                onClick={handleApplySelection}
                className="ml-auto"
              >
                Apply {selectedJobs.length} job
                {selectedJobs.length > 1 ? 's' : ''} to form
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
